"use client";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useMemo, useState } from "react";
import StepProgress from "../others/StepProgress";
import StepProgressMobile from "../others/StepProgressMobile";
import { lockBodyScroll, unlockBodyScroll } from "@/utils/commonFunctions";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { closeModal } from "@/redux/features/GoldInvestModal/goldInvestModalSlice";

import Icon from "@/components/icons/Icons";
import { openHelpModal } from "@/redux/features/HelpModal/helpModalSlice";
import { useLoginModal } from "@/utils/useLoginModal";
import { apiPost } from "@/utils/apiClient";
import { addToast } from "@/components/ui/CustomToastManager";
import { useRouter } from "next/navigation";
import { useIsMobile } from "@/utils/useIsMobile";
import InvestGoldFormStep1 from "../step/InvestGoldFormStep1";
import InvestGoldFormStep2 from "../step/InvestGoldFormStep2";
import InvestGoldFormStep3 from "../step/InvestGoldFormStep3";
import { DASHBOARD_ROOT } from "@/utils/gloableVariables";

type Step = {
  id: number;
  label: string;
};

const steps: Step[] = [
  { id: 1, label: "Select Your Tenure & SIP Amount" },
  { id: 2, label: "Select Your SIP Scheme" },
  { id: 3, label: "Summary" },
];

const stepsMobile: Step[] = [
  { id: 1, label: "Step 1" },
  { id: 2, label: "Step 2" },
  { id: 3, label: "Step 3" },
];

type FundItem = {
  id?: number;
  scheme_name?: string;
  scheme_code?: string;
  amc_code?: string;
  amc?: string;
  url?: string;
  category_type?: string;
  asset_id?: number;
  sip_allowed?: number;
  lump_allowed?: number;
  minimum_sip_invest?: number;
  minimum_investment?: number;
  six_month_abs?: number;
  one_year_abs?: number;
  three_year?: number;
};

type APIResponse = {
  status: boolean;
  message: string;
  cart_id: number;
};

export default function GoldInvestModal({
  schemeData,
  onValuesChange,
}: {
  schemeData?: FundItem[];
  onValuesChange?: (values: { tenure: number; sipAmount: number }) => void;
}) {
  const dispatch = useDispatch();
  const {
    isOpen,
    amount: reduxAmount,
    tenure: reduxTenure,
    src: source,
  } = useSelector((state: RootState) => state.goldInvestModal);

  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [sipAmount, setSipAmount] = useState(100);
  const [tenure, setTenure] = useState(8);
  const [sipDate, setSipDate] = useState(1);

  const [isLoading, setIsLoading] = useState(false);
  const { isLoggedIn, open, setModalKey } = useLoginModal();
  const [selectedFund, setSelectedFund] = useState<FundItem | null>(null);
  const [errorForm, setErrorForm] = useState(false);
  const [errorFundSelect, setErrorFundSelect] = useState(false);

  const [type, setType] = useState<"sip" | "lumpsum">("sip");

  const smallDesktopDevice = useIsMobile(1281);
  const MIN_SIP_AMOUNT = 100;
  const route = useRouter();

  useEffect(() => {
    if (isOpen) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
      resetForms();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    // Always apply the redux values when modal opens
    if (reduxTenure !== undefined && reduxTenure !== null) {
      setTenure(reduxTenure);
    }

    if (reduxAmount !== undefined && reduxAmount !== null) {
      setSipAmount(reduxAmount);
    }

    // Go to step 2 ONLY if any value exists
    if (
      reduxTenure !== undefined &&
      reduxTenure !== null &&
      reduxAmount !== undefined &&
      reduxAmount !== null
    ) {
      setCurrentStep(2);
      setDirection(1);
    }
  }, [isOpen]);

  const resetForms = () => {
    setCurrentStep(1);
    setDirection(1);
    setSipAmount(100);
    setTenure(8);
    setSipDate(1);
    setSelectedFund(null);
    setErrorForm(false);
    setErrorFundSelect(false);
    setType("sip");
    setIsLoading(false);
  };

  const filteredSchemes = useMemo(() => {
    if (!schemeData) return [];

    return schemeData.filter((fund) => {
      if (type === "sip") return fund.sip_allowed === 1;
      if (type === "lumpsum") return fund.lump_allowed === 1;
      return false;
    });
  }, [schemeData, type]);

  if (!schemeData) return;

  const handleCartAction = async () => {
    if (!isLoggedIn) {
      open();
      setModalKey("gold-investment-modal");
      setIsLoading(false);
      return;
    }

    const inv_type = type === "sip" ? "SIP" : "Lumpsum";

    setIsLoading(true);

    const payload = {
      scheme_code: selectedFund?.scheme_code || "",
      inv_type: inv_type.toLowerCase(),
      amount: sipAmount?.toString() || "",
      sip_date: sipDate,
      offset: "",
      addnl_investment: "",
      folio_no: "",
      source: source ?? "buy-digital-gold",
      medium: "Web",
      folio: "",
      tenure,
    };

    try {
      const res: APIResponse = await apiPost("addToCart", payload);

      if (res.status) {
        route.push(`${DASHBOARD_ROOT}single-buy/${res.cart_id}`);
      } else {
        addToast({
          title: "Add To Cart",
          description: "Failed to add to cart!",
          status: "failed",
        });
      }
    } catch (err) {
      console.error(err);
      addToast({
        title: "Add To Cart",
        description: "Something Went Wrong!",
        status: "failed",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const next = () => {
    if (currentStep === 1) {
      if (tenure === 0 || sipAmount < MIN_SIP_AMOUNT) {
        setErrorForm(true);
        return;
      }
      setDirection(1);
    }

    if (currentStep === 2) {
      if (!selectedFund) {
        setErrorFundSelect(true);
        return;
      }
      setDirection(1);
    }

    if (currentStep === 3) {
      handleCartAction();
    }
    setCurrentStep((prev) => Math.min(prev + 1, steps.length));
  };

  const prev = () => {
    setDirection(-1);
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const isInvalid =
    currentStep === 1 && (sipAmount < MIN_SIP_AMOUNT || tenure === 0);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            className="fixed flex items-center justify-center inset-0 bg-primaryBlack/60 backdrop-blur-lg z-max"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="w-full section"
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.85 }}
              transition={{
                duration: 0.25,
                ease: "easeOut",
              }}
            >
              <div className="hidden 2xl:flex items-center justify-end gap-2 text-white ml-auto mb-5">
                <button
                  onClick={() => dispatch(openHelpModal())}
                  className="hidden 2md:inline-block relative rounded-full font-semibold py-2.5 px-4 text-sm leading-4 border transition-all duration-400 ease-in-out bg-transparent text-white border-white hover:text-primaryBlack hover:bg-[#bbbbbb]"
                >
                  Need Help?
                </button>

                <button
                  onClick={() => dispatch(closeModal())}
                  aria-label="Close Popup"
                  className="flex group w-10 h-10 bg-[#FFFFFF33] rounded-full items-center justify-center"
                >
                  <Icon.popupCloseIcon className="w-5 h-5 text-white group-hover:text-[#ffd90e] rotate-45 transition" />
                </button>
              </div>

              <div className="flex flex-col 2xl:flex-row 2xl:items-start justify-between gap-5">
                {/* Step Section */}
                <div className="2xl:space-y-3 shrink-0">
                  <div className="hidden 2xl:block px-5 py-2 text-white font-semibold text-3xl border-b border-dotted">
                    Digital Gold
                  </div>
                  {smallDesktopDevice ? (
                    <StepProgressMobile
                      steps={stepsMobile}
                      currentStep={currentStep}
                      onStepChange={setCurrentStep}
                    />
                  ) : (
                    <StepProgress
                      steps={steps}
                      currentStep={currentStep}
                      onStepChange={setCurrentStep}
                      className="hidden 2xl:block"
                    />
                  )}
                </div>

                {/* Investment Process */}
                <div className="w-full space-y-3 sm:space-y-5 max-w-210.5 mx-auto 2xl:mr-0">
                  <div className="bg-[#101010] text-white w-full h-500 border border-[#C89900] rounded-xl sm:rounded-6xl shadow-[-0.9px_5.41px_35.6px_7px_#C8990040] relative overflow-hidden">
                    <div className="text-2xs font-medium text-white/60 mb-2.5 sm:mb-3.5 px-4 pt-3 xs:px-6 xs:pt-6 flex items-center justify-between">
                      <span className="leading-4">
                        Question {currentStep} of {steps.length}
                      </span>
                      <button
                        onClick={() => dispatch(closeModal())}
                        aria-label="Close Popup"
                        className="group w-4.5 h-4.5 mx-3 flex xs:hidden items-center justify-center"
                      >
                        <Icon.popupCloseIcon className="w-full h-full text-white rotate-45 transition" />
                      </button>
                    </div>

                    <AnimatePresence mode="wait" custom={direction}>
                      {currentStep === 1 && (
                        <InvestGoldFormStep1
                          key="step1"
                          direction={direction}
                          tenure={reduxTenure ?? tenure}
                          error={errorForm}
                          setTenure={setTenure}
                          sipAmount={sipAmount}
                          setSipAmount={(v) => {
                            setSipAmount(v);
                            onValuesChange?.({ tenure, sipAmount: v });
                          }}
                          mode={type}
                          onChangeMode={(m) => {
                            setType(m);
                          }}
                        />
                      )}

                      {currentStep === 2 && (
                        <InvestGoldFormStep2
                          key="step2"
                          direction={direction}
                          investType={type}
                          schemeData={filteredSchemes || []}
                          onSelectFund={(fund) => {
                            setSelectedFund(fund);
                            setErrorFundSelect(false);
                          }}
                          selectedFund={selectedFund}
                          error={errorFundSelect}
                        />
                      )}

                      {currentStep === 3 && (
                        <>
                          <InvestGoldFormStep3
                            key="step3"
                            direction={direction}
                            amcCode={selectedFund?.amc_code}
                            schemeName={selectedFund?.scheme_name}
                            mode={type}
                            sipAmount={sipAmount}
                            tenure={tenure}
                            selectedDate={sipDate}
                            onDateChange={(d) => setSipDate(d)}
                          />
                        </>
                      )}
                    </AnimatePresence>
                  </div>
                  <div className="flex gap-4 items-center justify-center">
                    <button
                      onClick={prev}
                      aria-label="Back Button"
                      disabled={currentStep === 1}
                      className={`flex items-center gap-2 border-2 sm:text-xl font-semibold border-[#C89900] px-4 sm:px-9 py-1.5 sm:py-2.5 bg-transparent rounded-full text-white transition duration-300 ${currentStep === 1 ? "opacity-70" : "lg:hover:bg-[#c8990036]"}`}
                    >
                      <Icon.rightArrow className="w-6 h-6 rotate-180" />
                      Back
                    </button>

                    <button
                      onClick={next}
                      disabled={isLoading || isInvalid}
                      aria-label="Continue Button"
                      className={`border-2 sm:text-xl font-semibold border-[#C89900] w-full max-w-48 sm:max-w-80 py-1.5 sm:py-2.5 bg-[#C89900] rounded-full text-white transition duration-300 ${isInvalid ? "opacity-75 pointer-events-none" : "lg:hover:bg-transparent"}`}
                    >
                      {!isLoading ? (
                        "Continue"
                      ) : (
                        <Icon.buttonLoaderSecondary className="text-black! mx-auto" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
